import React, { useState, useEffect } from "react";
import { Col, Container, Row, Card, CardBody } from "reactstrap";
import { Button } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import { FaTable, FaListUl, FaEdit, FaArrowLeft, FaEye, FaExchangeAlt, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import { Pagination } from "@mui/material";
import Loader from "../../Helper/Loader";
import config from "../../../config";
import ReviewModal from "./ReviewModal";
import VendorModal from "./VendorModal";
import QuickActionConfirmModal from "./QuickActionConfirmModal";
import Breadcrumbs from "../../Helper/Breadcrumbs";
import CustomSearch from "../../Helper/CustomSearch";

const icon = `${process.env.PUBLIC_URL}/img/images/order-images.jpg`;

const EventApprovalIndex = () => {
    const history = useHistory();
    const location = useLocation();
    const [data, setData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedVendorId, setSelectedVendorId] = useState(null);
    const [selectedVendorMeta, setSelectedVendorMeta] = useState({ status: null, latestOnboardingRequestId: null });
    const [view, setView] = useState("list");

    // New state for vendor modal and quick actions
    const [vendorModalOpen, setVendorModalOpen] = useState(false);
    const [vendorModalMode, setVendorModalMode] = useState('details');
    const [vendorDiffData, setVendorDiffData] = useState(null);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    // Default type based on URL
    const [approvalType, setApprovalType] = useState(
        location.pathname.includes("pendingvendor-list") ? "PENDING" : "APPROVED"
    );

    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [page, setPage] = useState({
        page: 1,
        count: 1,
        total: 0,
    });

    const tokenKey = {
        headers: { Authorization: "Token " + sessionStorage.getItem("tokenKey") },
    };

    // Effect to update validation type if URL changes (back/forward browser nav)
    useEffect(() => {
        setApprovalType(location.pathname.includes("pendingvendor-list") ? "PENDING" : "APPROVED");
    }, [location.pathname]);


    const manipulateData = (results, totalCount = 0, totalPages = 1, currentPage = 1) => {
        // Normalization: Map operating_cities to primary_city if primary_city is missing
        // Also ensure primary_city is a string name
        const normalizedResults = results.map(item => {
            let primaryCity = item.primary_city;

            // If primary_city is an object, extract the name
            if (primaryCity && typeof primaryCity === 'object') {
                primaryCity = primaryCity.name || primaryCity.city_name || primaryCity.label || '';
            }

            if (!primaryCity && item.operating_cities && item.operating_cities.length > 0) {
                // Use the first city as primary for display if missing
                primaryCity = item.operating_cities[0].name || item.operating_cities[0].city_name || item.operating_cities[0].label || '';
            }

            return { ...item, primary_city: primaryCity };
        });

        setData(normalizedResults); // Store current page raw data
        setTableData(normalizedResults);

        setPage({
            page: currentPage,
            count: totalPages,
            total: totalCount
        });
    };

    const fetchData = async (pageNum = 1, search = searchTerm, status = statusFilter) => {
        setLoading(true);
        try {
            let url;
            const params = {
                page: pageNum,
                page_size: 20,
            };

            if (search) params.search = search;
            if (status) params.status = status;

            if (approvalType === "APPROVED") {
                url = `${config.service}/v2/event-vendors/admin/profiles/`;
            } else {
                url = `${config.service}/v2/event-vendors/admin/list/`;
            }

            const res = await axios.get(url, { ...tokenKey, params });
            if (res.data) {
                let results = [];
                let totalCount = 0;
                let totalPages = 1;

                if (approvalType === "APPROVED") {
                    results = res.data.profiles || [];
                    totalCount = res.data.total_count || results.length;
                    totalPages = res.data.total_pages || 1;
                    results = results.map(r => ({ ...r, status: r.status || 'APPROVED' }));
                } else {
                    results = Array.isArray(res.data.results) ? res.data.results : (Array.isArray(res.data) ? res.data : []);
                    totalCount = res.data.total_count || results.length;
                    totalPages = res.data.total_pages || 1;
                }
                manipulateData(results, totalCount, totalPages, pageNum);
            } else {
                setData([]);
                setTableData([]);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setData([]);
            setTableData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setSearchTerm("");
        setStatusFilter("");
        fetchData(1, "", "");
    }, [approvalType]); // Refetch and reset search/filter when approvalType changes (route change)

    const handleAction = (row) => {
        setSelectedVendorId(row?.id ?? null);
        setSelectedVendorMeta({
            status: row?.status ?? null,
            latestOnboardingRequestId: row?.latest_onboarding_request_id ?? null,
        });
        setModalOpen(true);
    };

    // Fetch vendor data from diff API (returns both entry and diff)
    const fetchVendorData = async (vendorId, parentId = null) => {
        try {
            // Use different endpoint for APPROVED vs PENDING vendors
            let url;
            if (approvalType === "APPROVED") {
                url = `${config.service}/v2/event-vendors/admin/approved/${vendorId}/`;
                const res = await axios.get(url, tokenKey);
                return res.data;
            } else {
                url = `${config.service}/v2/event-vendors/admin/${vendorId}/diff/`;
                const diffRes = await axios.get(url, tokenKey);
                let diffData = diffRes.data;

                // Determine if it's a new submission or an update
                const isNewSubmission = diffData.is_new_submission ?? (diffData.parent_profile === null && diffData.parent_profile_id === null);

                let fullEntryData = null;

                try {
                    if (isNewSubmission) {
                        // New submission: Fetch the pending entry itself to get full details
                        const entryUrl = `${config.service}/v2/event-vendors/admin/${vendorId}/`;
                        const entryRes = await axios.get(entryUrl, tokenKey);
                        fullEntryData = entryRes.data;
                    } else {
                        // Update: Fetch the parent (approved) profile
                        const pid = parentId || diffData.parent_profile || diffData.parent_profile_id;
                        if (pid) {
                            const approvedUrl = `${config.service}/v2/event-vendors/admin/approved/${pid}/`;
                            const approvedRes = await axios.get(approvedUrl, tokenKey);
                            fullEntryData = approvedRes.data;
                        }
                    }
                } catch (err) {
                    console.warn("Failed to fetch full entry details:", err);
                }

                if (fullEntryData) {
                    // Merge: Use fetched data as 'entry' (current details), keep 'diff' from diff API
                    // For new submissions, diffData might already satisfy structure, but explicit fetch ensures full detail
                    return {
                        ...diffData,
                        entry: fullEntryData,
                        pendingId: vendorId,
                        pendingStatus: isNewSubmission ? fullEntryData.status : 'PENDING_APPROVAL',
                        pendingLatestOnboardingRequestId: isNewSubmission ? fullEntryData.latest_onboarding_request_id : diffData.latest_onboarding_request_id
                    };
                }

                return diffData;
            }
        } catch (error) {
            console.error("Error fetching vendor data:", error);
            toast.error("Failed to fetch vendor data");
            return null;
        }
    };

    // Handle view details - opens modal in details mode
    const handleViewDetails = async (vendorId, parentId = null) => {
        const data = await fetchVendorData(vendorId, parentId);
        if (data) {
            setVendorDiffData(data);
            setVendorModalMode('details');
            setVendorModalOpen(true);
        }
    };

    // Handle view changes - opens modal in changes mode
    const handleViewChanges = async (vendorId, parentId = null) => {
        const data = await fetchVendorData(vendorId, parentId);
        if (data) {
            setVendorDiffData(data);
            setVendorModalMode('changes');
            setVendorModalOpen(true);
        }
    };

    // Handle quick action (approve/reject)
    const handleQuickAction = (type, vendorId, vendorName, vendorStatus = null, latestOnboardingRequestId = null) => {
        setConfirmAction({ type, vendorId, vendorName, vendorStatus, latestOnboardingRequestId });
        setConfirmModalOpen(true);
    };

    // Execute quick action after confirmation
    const executeQuickAction = async (remarks = '') => {
        if (!confirmAction) return;

        const { type, vendorId, vendorStatus, latestOnboardingRequestId } = confirmAction;
        const action = type === 'APPROVE' ? 'APPROVE' : 'REJECT';

        try {
            const actionId = vendorStatus === "ACTIVE" && latestOnboardingRequestId ? latestOnboardingRequestId : vendorId;
            const url = `${config.service}/v2/event-vendors/admin/${actionId}/action/`;
            await axios.post(url, { action, remarks }, tokenKey);

            toast.success(`${type === 'APPROVE' ? 'Approved' : 'Rejected'} successfully`);
            setConfirmModalOpen(false);
            setConfirmAction(null);
            fetchData(); // Refresh the list
        } catch (error) {
            console.error("Error executing action:", error);
            toast.error(error.response?.data?.message || `Failed to ${type.toLowerCase()}`);
        }
    };

    // Handle Request Edit
    const handleRequestEdit = () => {
        if (selectedVendorId || vendorDiffData?.entry?.id) {
            const vid = selectedVendorId || vendorDiffData?.entry?.id;
            const vname = vendorDiffData?.entry?.business_name || '';
            // Close vendor modal if open
            if (vendorModalOpen) setVendorModalOpen(false);

            // Open confirm modal with REQUEST_EDIT action
            setConfirmAction({
                type: 'REQUEST_EDIT',
                vendorId: vid,
                vendorName: vname,
                vendorStatus: selectedVendorMeta?.status ?? vendorDiffData?.entry?.status ?? null,
                latestOnboardingRequestId: selectedVendorMeta?.latestOnboardingRequestId ?? vendorDiffData?.entry?.latest_onboarding_request_id ?? null,
            });
            setConfirmModalOpen(true);
        }
    };

    // Switch vendor modal mode
    const handleVendorModalSwitch = (newMode) => {
        setVendorModalMode(newMode);
    };

    // Open review modal from vendor modal
    const handleOpenReviewFromModal = () => {
        if (vendorDiffData?.entry?.id) {
            setSelectedVendorId(vendorDiffData.entry.id);
            setSelectedVendorMeta({
                status: vendorDiffData?.entry?.status ?? null,
                latestOnboardingRequestId: vendorDiffData?.entry?.latest_onboarding_request_id ?? null,
            });
            setModalOpen(true);
        }
    };

    const handleSearch = (term) => {
        setSearchTerm(term);
        fetchData(1, term);
    };

    const handlePageChange = (event, value) => {
        fetchData(value);
    };

    const formatUTC = (dtStr) => {
        if (!dtStr) return "N/A";
        // Backend format: "DD/MM/YYYY HH:MM:SSxxxxxx" (sub-second digits appended without separator)
        const match = dtStr.match(/^(\d{2})\/(\d{2})\/(\d{4})\s(\d{2}):(\d{2}):(\d{2})/);
        if (!match) return "N/A";
        const [, dd, mm, yyyy, hh, min, ss] = match;
        return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss} UTC`;
    };

    const navigateToPending = () => {
        history.push("/pendingvendor-list");
    };

    const navigateToApproved = () => {
        history.push("/event-approval");
    };

    const columns = [
        {
            dataField: "id",
            text: "ID",
            headerStyle: { width: "60px" },
        },
        {
            dataField: "business_name",
            text: "Business Name",
            formatter: (cell, row) => cell || row.phone_number || "N/A"
        },
        {
            dataField: "category",
            text: "Category",
            formatter: (cell) => cell || "N/A"
        },
        {
            dataField: "primary_city",
            text: "City",
            formatter: (cell) => cell || "N/A"
        },
        {
            dataField: "status",
            text: "Status",
            formatter: (cell) => {
                const isApproved = cell === 'APPROVED' || cell === 'MIGRATED' || cell === 'Migrated';
                const displayStatus = (cell === 'MIGRATED' || cell === 'Migrated') ? 'APPROVED' : cell;
                const badgeClass = isApproved ? 'success' : cell === 'DRAFT' ? 'secondary' : 'warning';

                return (
                    <span className={`badge badge-${badgeClass}`}>
                        {displayStatus}
                    </span>
                );
            }
        },
        {
            dataField: "created_at",
            text: "Submitted (UTC)",
            formatter: (cell) => formatUTC(cell)
        },
        {
            dataField: "actions",
            text: "Actions",
            formatter: (cell, row) => (
                <div className="tableActions">
                    <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleAction(row)}
                    >
                        Review
                    </Button>
                </div>
            ),
        },
    ];

    // Standard pagination has been moved to manual control
    const paginationOptions = {
        hideSizePerPage: true,
        hidePageListOnlyOnePage: true,
        custom: true,
    };

    // Dynamic Breadcrumb
    const breadcrumbData = [
        { title: "Vendor", href: "#" },
        { title: approvalType === "APPROVED" ? "Event Approval" : "Pending Approvals", href: "#" }
    ];

    return (
        <Container className="vendor">
            <Row>
                <Col md={12} className="contBody" style={{ padding: '0 15px' }}>
                    <div className="card__title pageNav">
                        <div className="bold-text">
                            <Breadcrumbs data={breadcrumbData} />
                        </div>
                    </div>

                    <Row className="formBtnsDiv mar-b-10">
                        <Col md={8}>
                            {approvalType === "APPROVED" ? (
                                <Button
                                    variant="primary"
                                    onClick={navigateToPending}
                                >
                                    Pending Approvals
                                </Button>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <Button
                                        variant="outline-primary"
                                        onClick={navigateToApproved}
                                    >
                                        <FaArrowLeft style={{ marginRight: "5px" }} /> Back to Approved List
                                    </Button>
                                </div>
                            )}
                        </Col>
                        <Col md={4}>
                            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                <CustomSearch callbackFn={handleSearch} />
                                {approvalType !== "APPROVED" && (
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => {
                                            setStatusFilter(e.target.value);
                                            fetchData(1, searchTerm, e.target.value);
                                        }}
                                        style={{
                                            padding: "6px 10px",
                                            border: "1px solid #ccc",
                                            borderRadius: 4,
                                            fontSize: 13,
                                            height: 36,
                                            cursor: "pointer",
                                            minWidth: 150,
                                        }}
                                    >
                                        <option value="">All Status</option>
                                        <option value="DRAFT">Draft</option>
                                        <option value="OTP_PENDING">OTP Pending</option>
                                        <option value="PENDING_APPROVAL">Pending Approval</option>
                                        <option value="EDIT_REQUESTED">Edit Requested</option>
                                    </select>
                                )}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <div className="viewIcons">
                                <span>
                                    <FaListUl
                                        title="Card View"
                                        onClick={() => setView("list")}
                                        style={{ color: view === 'list' ? '#70bbfd' : 'inherit' }}
                                    />
                                </span>
                                <span>
                                    <FaTable
                                        title="List View"
                                        onClick={() => setView("table")}
                                        style={{ color: view === 'table' ? '#70bbfd' : 'inherit' }}
                                    />
                                </span>
                            </div>
                        </Col>
                    </Row>

                    <Row className="vendorList">
                        <Loader loading={loading}>
                            {view === "list" ? (
                                tableData.length > 0 ? (
                                    tableData.map((row) => {
                                        let coverImg = row.cover_image || icon;
                                        const isRowApproved = row.status === 'APPROVED' || row.status === 'MIGRATED' || row.status === 'Migrated';
                                        const rowDisplayStatus = (row.status === 'MIGRATED' || row.status === 'Migrated') ? 'APPROVED' : row.status;
                                        const isPending = row.status === "PENDING_APPROVAL";

                                        return (
                                            <Col xl={4} lg={6} md={6} sm={12} className="my-3" key={row.id}>
                                                <div className="vendor-card " >
                                                    {/* Card Header with Status Badge */}
                                                    <div className="vendor-card-header">
                                                        <span className={`vendor-status-badge status-${rowDisplayStatus.toLowerCase()}`}>
                                                            {rowDisplayStatus}
                                                        </span>
                                                        {isPending && row.parent_profile && (
                                                            <span className="vendor-update-badge">UPDATE</span>
                                                        )}
                                                    </div>

                                                    <div className="vendor-card-body">
                                                        {/* Image Section */}
                                                        <div className="vendor-image-container">
                                                            <img
                                                                className="vendor-image"
                                                                src={coverImg}
                                                                alt={row.business_name || "Vendor"}
                                                                onError={(e) => { e.target.src = icon }}
                                                            />
                                                        </div>

                                                        {/* Content Section */}
                                                        <div className="vendor-content">
                                                            <h3
                                                                className="vendor-name"
                                                                onClick={isPending ? () => handleViewDetails(row.id, row.parent_profile) : undefined}
                                                                title={isPending ? "Click to view details" : undefined}
                                                                style={isPending ? { cursor: 'pointer' } : {}}
                                                            >
                                                                {row.business_name || row.phone_number || "No Name"}
                                                            </h3>

                                                            <div className="vendor-meta">
                                                                <div className="meta-item">
                                                                    <span className="meta-label">Category</span>
                                                                    <span className="meta-value">{row.category || "N/A"}</span>
                                                                </div>
                                                                <div className="meta-item">
                                                                    <span className="meta-label">Location</span>
                                                                    <span className="meta-value">{row.primary_city || "N/A"}</span>
                                                                </div>
                                                                <div className="meta-item">
                                                                    <span className="meta-label">Contact</span>
                                                                    <span className="meta-value">{row.phone_number || "N/A"}</span>
                                                                </div>
                                                                <div className="meta-item">
                                                                    <span className="meta-label">Submitted</span>
                                                                    <span className="meta-value" style={{ fontSize: '11px', color: '#888' }}>{formatUTC(row.created_at)}</span>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Actions Section */}
                                                        <div className="vendor-actions">
                                                            <button
                                                                className="action-btn action-view"
                                                                onClick={() => handleViewDetails(row.id, row.parent_profile)}
                                                                title="View Details"
                                                                style={approvalType === "APPROVED" ? { width: '100%' } : {}}
                                                            >
                                                                <FaEye />
                                                                <span>{approvalType === "APPROVED" ? 'View Details' : 'View'}</span>
                                                            </button>

                                                            {approvalType === "PENDING" && (
                                                                <>
                                                                    {/* Removed Changes Button - now inside Modal */}

                                                                    {isPending && (
                                                                        <>
                                                                            <button
                                                                                className="action-btn action-approve"
                                                                                onClick={() => handleQuickAction('APPROVE', row.id, row.business_name, row.status, row.latest_onboarding_request_id)}
                                                                                title="Quick Approve"
                                                                            >
                                                                                <FaCheckCircle />
                                                                                <span>Approve</span>
                                                                            </button>

                                                                            <button
                                                                                className="action-btn action-reject"
                                                                                onClick={() => handleQuickAction('REJECT', row.id, row.business_name, row.status, row.latest_onboarding_request_id)}
                                                                                title="Quick Reject"
                                                                            >
                                                                                <FaTimesCircle />
                                                                                <span>Reject</span>
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Col>
                                        );
                                    })
                                ) : (
                                    <div className="textCenter">No data found</div>
                                )
                            ) : (
                                <div className="table-container">
                                    <BootstrapTable
                                        keyField="id"
                                        data={tableData}
                                        columns={columns}
                                        bootstrap4
                                        wrapperClasses="table-responsive"
                                        noDataIndication="No data found"
                                    />
                                </div>
                            )}
                        </Loader>
                    </Row>

                    {page.count > 1 && (
                        <Row className="mar-t-30 mar-b-20 justify-content-center">
                            <Pagination
                                count={page.count}
                                page={page.page}
                                onChange={handlePageChange}
                                shape="rounded"
                                sx={{
                                    '& .Mui-selected': {
                                        backgroundColor: '#EC628A !important',
                                        color: '#ffffff !important',
                                    },
                                    '& .MuiPaginationItem-root:hover': {
                                        backgroundColor: 'rgba(236, 98, 138, 0.1)',
                                    }
                                }}
                            />
                        </Row>
                    )}
                </Col>
            </Row>

            {selectedVendorId && (
                <ReviewModal
                    isOpen={modalOpen}
                    handleClose={() => {
                        setModalOpen(false);
                        setSelectedVendorId(null);
                        setSelectedVendorMeta({ status: null, latestOnboardingRequestId: null });
                    }}
                    vendorId={selectedVendorId}
                    vendorStatus={selectedVendorMeta?.status}
                    latestOnboardingRequestId={selectedVendorMeta?.latestOnboardingRequestId}
                    onSuccess={fetchData}
                />
            )}

            {/* Vendor Modal for Details and Changes */}
            <VendorModal
                source="event-approval"
                isOpen={vendorModalOpen}
                onClose={() => {
                    setVendorModalOpen(false);
                    setVendorDiffData(null);
                }}
                data={vendorDiffData}
                mode={vendorModalMode}
                onSwitchMode={handleVendorModalSwitch}
                onOpenReview={handleOpenReviewFromModal}
                isPending={vendorDiffData?.entry?.status === "PENDING_APPROVAL" || vendorDiffData?.entry?.status === "EDIT_REQUESTED" || vendorDiffData?.entry?.status === "ACTIVE"}
                hideApproveReject={approvalType === "APPROVED"}
                onApprove={() => {
                    if (vendorDiffData?.pendingId || vendorDiffData?.entry?.id) {
                        handleQuickAction(
                            'APPROVE',
                            vendorDiffData.pendingId || vendorDiffData.entry.id,
                            vendorDiffData.entry.business_name,
                            vendorDiffData.pendingStatus || vendorDiffData?.entry?.status,
                            vendorDiffData.pendingLatestOnboardingRequestId || vendorDiffData?.entry?.latest_onboarding_request_id
                        );
                    }

                }}
                onReject={() => {
                    if (vendorDiffData?.pendingId || vendorDiffData?.entry?.id) {
                        handleQuickAction(
                            'REJECT',
                            vendorDiffData.pendingId || vendorDiffData.entry.id,
                            vendorDiffData.entry.business_name,
                            vendorDiffData.pendingStatus || vendorDiffData?.entry?.status,
                            vendorDiffData.pendingLatestOnboardingRequestId || vendorDiffData?.entry?.latest_onboarding_request_id
                        );
                    }
                }}
                onRequestEdit={handleRequestEdit}
            />

            {/* Quick Action Confirmation Modal */}
            <QuickActionConfirmModal
                isOpen={confirmModalOpen}
                onClose={() => {
                    setConfirmModalOpen(false);
                    setConfirmAction(null);
                }}
                action={confirmAction?.type}
                vendorName={confirmAction?.vendorName}
                onConfirm={executeQuickAction}
            />

            <ToastContainer autoClose={3000} />
        </Container>
    );
};

export default EventApprovalIndex;
